import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Modal } from "react-bootstrap";
// Update your Fa imports to include:
import { 
  FaJava, FaReact, FaNodeJs, FaPhp, FaWordpress, FaArrowRight, 
  FaComments, FaUserGraduate, FaLaptopCode, FaCertificate, 
  FaBriefcase, FaCode, FaUsers, FaRocket  // Add these new icons
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import "./career.css";

const FuturisticCareerPage = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Floating orbs background component
  const FloatingOrbs = () => (
    <div className="orb-container">
      <div className="floating-orb orb-1"></div>
      <div className="floating-orb orb-2"></div>
      <div className="floating-orb orb-3"></div>
      <div className="grid-overlay"></div>
      <div className="scan-lines"></div>
    </div>
  );

  // Animated Counter Component
  const AnimatedCounter = ({ end, duration = 2, label }) => {
    const [count, setCount] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const ref = React.useRef(null);

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            let start = 0;
            const increment = end / (duration * 60);
            
            const timer = setInterval(() => {
              start += increment;
              if (start >= end) {
                setCount(end);
                clearInterval(timer);
              } else {
                setCount(Math.ceil(start));
              }
            }, 1000 / 60);
          }
        },
        { threshold: 0.1 }
      );

      if (ref.current) {
        observer.observe(ref.current);
      }

      return () => observer.disconnect();
    }, [end, duration]);

    return (
      <motion.div 
        ref={ref}
        className="counter-item glass-card"
        initial={{ opacity: 0, scale: 0.5 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="counter-number">{count}+</div>
        <div className="counter-label">{label}</div>
      </motion.div>
    );
  };

  // Glass Card Component
  const GlassCard = ({ children, className = "", hover = true, ...props }) => {
    return (
      <motion.div
        className={`glass-card ${className} ${hover ? 'hover-glow' : ''}`}
        whileHover={hover ? { y: -5 } : {}}
        transition={{ type: "spring", stiffness: 300 }}
        {...props}
      >
        {children}
      </motion.div>
    );
  };

  // Feature Card Component
  const FeatureCard = ({ icon, title, description, className = "" }) => {
    return (
      <motion.div
        className={`feature-card-futuristic ${className}`}
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <div className="feature-icon-futuristic">
          {icon}
        </div>
        <h4 className="gradient-text-blue text-glow">{title}</h4>
        <p className="text-secondary">{description}</p>
      </motion.div>
    );
  };

  // Internship Card Component
  const InternshipCard = ({ title, icon, desc, duration, skills, onClick }) => (
    <GlassCard className="internship-card-futuristic h-100">
      <div className="card-icon-container">
        <div className="feature-icon-futuristic">
          {icon}
        </div>
      </div>
      <h3 className="gradient-text-blue">{title}</h3>
      <p className="text-secondary">{desc}</p>
      
      <div className="duration-badge-futuristic">
        <span>{duration}</span>
      </div>
      
      <div className="skills-grid-futuristic">
        {skills.map((skill, i) => (
          <span key={i} className="skill-tag-futuristic">{skill}</span>
        ))}
      </div>
      
      <Button 
        className="btn-futuristic w-100 mt-3"
        onClick={onClick}
      >
        Apply Now <FaArrowRight className="ms-2" />
      </Button>
    </GlassCard>
  );

  // Futuristic Modal
  const FuturisticModal = ({ show, onHide, title, children }) => (
    <Modal 
      show={show} 
      onHide={onHide} 
      dialogClassName="futuristic-modal"
      centered
    >
      <Modal.Header className="glass-card">
        <Modal.Title className="gradient-text">{title}</Modal.Title>
        <Button variant="close" onClick={onHide} className="btn-close-white" />
      </Modal.Header>
      <Modal.Body className="glass-card">
        {children}
      </Modal.Body>
    </Modal>
  );

  const internships = [
    {
      title: "Java Developer Intern",
      icon: <FaJava size={30} />,
      desc: "Master Core Java concepts, OOP principles, and backend development with hands-on projects under expert guidance.",
      duration: "6 Months",
      skills: ["Java Core Concepts", "Java Advanced Concepts", "Spring Boot", "Microservices"],
    },
    {
      title: "Full Stack Developer (MERN)",
      icon: <FaReact size={30} />,
      desc: "Build complete web applications using React, Node.js, Express, and MongoDB. Develop real-world projects from scratch.",
      duration: "6 Months",
      skills: ["React.js", "Node.js", "Express", "MongoDB", "Redux", "JWT Auth"],
    },
    {
      title: "Interview Preparation",
      icon: <FaLaptopCode size={30} />,
      desc: "Ace MERN stack interviews with targeted problem-solving, full-stack project discussions, and HR guidance.",
      duration: "2 Months",
      skills: [
        "React.js Interview Qs",
        "Node.js Coding Rounds",
        "MongoDB Queries",
        "System Design",
        "Mock Interviews",
        "Soft Skills"
      ],
    },
    {
      title: "Soft Skills",
      icon: <FaComments size={30} />,
      desc: "Develop essential soft skills that complement your technical expertise and prepare you for workplace success.",
      duration: "Ongoing",
      skills: [
        "Communication Skills",
        "Teamwork",
        "Problem Solving",
        "Leadership",
        "Time Management"
      ],
    }
  ];

  const features = [
    {
      icon: <FaUserGraduate />,
      title: "1:1 Mentorship",
      desc: "Personalized guidance from industry experts"
    },
    {
      icon: <FaLaptopCode />,
      title: "Live Projects",
      desc: "Work on real-world applications"
    },
    {
      icon: <FaCertificate />,
      title: "Certification",
      desc: "Recognized completion certificate"
    },
    {
      icon: <FaBriefcase />,
      title: "Placement Support",
      desc: "Interview preparation and job assistance"
    }
  ];

  const stats = [
    { number: 500, label: "Students Trained" },
    { number: 95, label: "Placement Rate" },
    { number: 50, label: "Partner Companies" },
    { number: 100, label: "Projects Completed" }
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="futuristic-career-page">
      <FloatingOrbs />
      
      {/* Hero Section */}
      <section className="hero-section-futuristic">
        <Container>
          <motion.div 
            className="hero-content-futuristic"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-title-futuristic py-4">
              Our <span className="gradient-text">Internship & Training</span> Programs
            </h1>
            <p className="hero-subtitle-futuristic">
              Hands-on training with industry experts and real-world projects
            </p>
            
            <Row className="g-4 mb-5">
              {features.map((feature, index) => (
                <Col lg={3} md={6} key={index}>
                  <FeatureCard
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.desc}
                  />
                </Col>
              ))}
            </Row>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button 
                className="btn-futuristic btn-lg"
                onClick={() => document.getElementById("internships").scrollIntoView({ behavior: 'smooth' })}
              >
                Explore Programs <FaArrowRight className="ms-2" />
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </section>

      {/* Stats Section */}
      {/* Professional Training Approach Section */}
<section className="training-approach-section">
  <Container>
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="text-center mb-5"
    >
      <h2 className="gradient-text display-5 fw-bold mb-3">Professional Training Methodology</h2>
      <p className="lead text-secondary">Learn by building real-world applications with industry best practices</p>
    </motion.div>

    <Row className="g-4">
      <Col lg={6}>
        <GlassCard className="p-4 h-100">
          <div className="approach-item">
            <div className="approach-icon">
              <FaLaptopCode size={40} />
            </div>
            <div className="approach-content">
              <h4 className="gradient-text-blue mb-3">Live Project Development</h4>
              <p className="text-secondary mb-3">
                Work on actual client projects from day one. Gain hands-on experience in:
              </p>
              <ul className="approach-list">
                <li>Real-world problem solving</li>
                <li>Industry-standard code practices</li>
                <li>Agile development methodology</li>
                <li>Version control with Git</li>
                <li>Code review processes</li>
              </ul>
            </div>
          </div>
        </GlassCard>
      </Col>
      
      <Col lg={6}>
        <GlassCard className="p-4 h-100">
          <div className="approach-item">
            <div className="approach-icon">
              <FaUserGraduate size={40} />
            </div>
            <div className="approach-content">
              <h4 className="gradient-text-blue mb-3">Industry Mentorship</h4>
              <p className="text-secondary mb-3">
                Learn directly from senior developers and tech leads with:
              </p>
              <ul className="approach-list">
                <li>1:1 personalized guidance</li>
                <li>Weekly code review sessions</li>
                <li>Career guidance and roadmap</li>
                <li>Interview preparation</li>
                <li>Professional development</li>
              </ul>
            </div>
          </div>
        </GlassCard>
      </Col>
    </Row>

    <Row className="g-4 mt-2">
      <Col lg={4} md={6}>
        <motion.div
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <GlassCard className="p-4 text-center h-100">
            <div className="benefit-icon">
              <FaCode size={30} />
            </div>
            <h5 className="gradient-text-blue mb-3">Production Ready Code</h5>
            <p className="text-secondary">
              Write clean, maintainable code that meets industry standards and deployment requirements
            </p>
          </GlassCard>
        </motion.div>
      </Col>
      
      <Col lg={4} md={6}>
        <motion.div
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <GlassCard className="p-4 text-center h-100">
            <div className="benefit-icon">
              <FaUsers size={30} />
            </div>
            <h5 className="gradient-text-blue mb-3">Team Collaboration</h5>
            <p className="text-secondary">
              Experience real team dynamics with daily standups, sprint planning, and collaborative development
            </p>
          </GlassCard>
        </motion.div>
      </Col>
      
      <Col lg={4} md={6}>
        <motion.div
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <GlassCard className="p-4 text-center h-100">
            <div className="benefit-icon">
              <FaRocket size={30} />
            </div>
            <h5 className="gradient-text-blue mb-3">Career Launchpad</h5>
            <p className="text-secondary">
              Build a strong portfolio with real projects that impress employers and accelerate your career
            </p>
          </GlassCard>
        </motion.div>
      </Col>
    </Row>
  </Container>
</section>

      {/* Internships Section */}
      <Container id="internships" className="py-5">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="text-center mb-5"
        >
          <h2 className="section-title-futuristic gradient-text">Choose Your Career Path</h2>
          <p className="section-subtitle-futuristic">Industry-relevant programs designed for your success</p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Row className="g-4">
            {internships.map((item, idx) => (
              <Col lg={6} key={idx}>
                <InternshipCard 
                  title={item.title}
                  icon={item.icon}
                  desc={item.desc}
                  duration={item.duration}
                  skills={item.skills}
                  onClick={() => setIsModalOpen(true)}
                />
              </Col>
            ))}
          </Row>
        </motion.div>
      </Container>

      {/* CTA Section */}
      <section className="cta-section-futuristic">
        <Container>
          <GlassCard className="cta-card-futuristic text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="gradient-text">Ready to transform your career?</h2>
              <p className="text-secondary">Limited seats available for our upcoming batch</p>
              <Button 
                className="btn-futuristic btn-lg"
                onClick={() => navigate("/contact")}
              >
                Apply Now <FaArrowRight className="ms-2" />
              </Button>
            </motion.div>
          </GlassCard>
        </Container>
      </section>

      {/* Demo Modal */}
      <FuturisticModal 
        show={isModalOpen} 
        onHide={() => setIsModalOpen(false)}
        title="Apply for Internship"
      >
        <p>This is a futuristic modal with the same design theme!</p>
        <div className="d-flex gap-2 mt-3">
          <Button className="btn-futuristic" onClick={() => setIsModalOpen(false)}>
            Close
          </Button>
          <Button className="btn-futuristic" onClick={() => navigate("/contact")}>
            Continue to Apply
          </Button>
        </div>
      </FuturisticModal>
    </div>
  );
};

export default FuturisticCareerPage;